import { useState } from "react";
import { trpc } from "@/utils/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const PRIORITY_LABELS: Record<string, string> = { p1: "P1 — Critical", p2: "P2 — High", p3: "P3 — Medium", p4: "P4 — Low" };
const PRIORITY_COLORS: Record<string, string> = { p1: "bg-[#FFE5E5] text-[#DC2626] border-[#FFE5E5]", p2: "bg-[#FFEDD5] text-[#EA580C] border-[#FFEDD5]", p3: "bg-[#FFF4E5] text-[#D97706] border-[#FFF4E5]", p4: "bg-[#E8F9F8] text-[#0D9488] border-[#E8F9F8]" };

export default function TicketTypeSettings() {
  const { data: types = [], isLoading, refetch } = trpc.tickets.ticketTypes.getAll.useQuery();
  const { data: categories = [] } = trpc.tickets.ticketCategories.getAll.useQuery();
  const createMut = trpc.tickets.ticketTypes.create.useMutation({ onSuccess: () => { refetch(); setOpen(false); toast.success("Ticket type created"); } });
  const updateMut = trpc.tickets.ticketTypes.update.useMutation({ onSuccess: () => { refetch(); setOpen(false); toast.success("Ticket type updated"); } });
  const deleteMut = trpc.tickets.ticketTypes.delete.useMutation({ onSuccess: () => { refetch(); toast.success("Ticket type deleted"); } });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ name: "", nameAr: "", categoryId: 0, defaultPriority: "p3" as string, requiresApproval: false });

  const openCreate = () => { setEditing(null); setForm({ name: "", nameAr: "", categoryId: categories[0]?.id ?? 0, defaultPriority: "p3", requiresApproval: false }); setOpen(true); };
  const openEdit = (t: any) => { setEditing(t); setForm({ name: t.name, nameAr: t.nameAr ?? "", categoryId: t.categoryId, defaultPriority: t.defaultPriority, requiresApproval: t.requiresApproval }); setOpen(true); };

  const handleSave = () => {
    if (editing) updateMut.mutate({ id: editing.id, ...form } as any);
    else createMut.mutate(form as any);
  };

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5B2C93]" /></div>;

  return (
    <div className="px-0 py-5 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#6B6B6B]">{types.length} type{types.length !== 1 ? "s" : ""} configured</p>
        <Button onClick={openCreate} size="sm" className="bg-[#5B2C93] hover:bg-[#4A2377] text-white gap-1.5"><Plus className="h-4 w-4" />Add Type</Button>
      </div>

      <div className="rounded-lg border border-[#E0E0E0] overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E0E0E0] bg-[#FAFAFA]">
            <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Name</th>
            <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Category</th>
            <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Priority</th>
            <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Approval</th>
            <th className="text-right px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Actions</th>
          </tr></thead>
          <tbody>
            {types.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-12 text-[#9CA3AF]">No ticket types configured</td></tr>
            ) : types.map((t: any) => {
              const cat = categories.find((c: any) => c.id === t.categoryId);
              return (
                <tr key={t.id} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#F5F8FF]">
                  <td className="px-6 py-3"><div className="font-medium text-[#2C2C2C]">{t.name}</div>{t.nameAr && <div className="text-xs text-[#9CA3AF] mt-0.5">{t.nameAr}</div>}</td>
                  <td className="px-6 py-3 text-[#6B6B6B]">{(cat as any)?.name ?? "—"}</td>
                  <td className="px-6 py-3"><Badge variant="outline" className={PRIORITY_COLORS[t.defaultPriority] ?? ""}>{PRIORITY_LABELS[t.defaultPriority] ?? t.defaultPriority}</Badge></td>
                  <td className="px-6 py-3">{t.requiresApproval ? <Badge variant="outline" className="bg-[#E8DCF5] text-[#5B2C93] border-[#E8DCF5]">Required</Badge> : <span className="text-[#9CA3AF]">—</span>}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" className="border-[#E0E0E0]" onClick={() => openEdit(t)}>Edit</Button>
                      <Button size="sm" variant="outline" className="text-[#DC2626] border-[#E0E0E0] hover:bg-[#FFE5E5]" onClick={() => { if (confirm("Delete this type?")) deleteMut.mutate({ id: t.id }); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editing ? "Edit" : "Create"} Ticket Type</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-sm font-medium">Name</label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" /></div>
            <div><label className="text-sm font-medium">Name (Arabic)</label><Input value={form.nameAr} onChange={e => setForm(f => ({ ...f, nameAr: e.target.value }))} className="mt-1" dir="rtl" /></div>
            <div><label className="text-sm font-medium">Category</label>
              <Select value={String(form.categoryId)} onValueChange={v => setForm(f => ({ ...f, categoryId: Number(v) }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{categories.map((c: any) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><label className="text-sm font-medium">Default Priority</label>
              <Select value={form.defaultPriority} onValueChange={v => setForm(f => ({ ...f, defaultPriority: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.requiresApproval} onChange={e => setForm(f => ({ ...f, requiresApproval: e.target.checked }))} className="rounded" />Requires approval workflow</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={handleSave} disabled={!form.name || !form.categoryId} className="bg-[#5B2C93] hover:bg-[#4A2377] text-white"><Save className="h-4 w-4 mr-2" />Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
